<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="true"
    :draggable="false"
    :style="{ width: 'min(560px, 92vw)' }"
    :pt="{ root: { style: 'max-height: 90vh; overflow-y: auto;' }, header: { style: 'border-bottom: 1px solid var(--p-surface-700); margin-bottom: 1rem;' } }"
    @update:visible="(v) => emit('update:visible', v)"
  >
    <!-- Header -->
    <template #header>
      <div class="dialog-header-title">
        <span class="material-icons dialog-header-icon">electrical_services</span>
        <div>
          <h3>{{ $t('mcpConnect.title') }}</h3>
          <p>{{ $t('mcpConnect.intro') }}</p>
        </div>
      </div>
    </template>

    <div class="dialog-body">

      <!-- URL -->
      <div class="section-label">
        <span class="material-icons">link</span>
        {{ $t('mcpConnect.urlLabel') }}
      </div>
      <div class="url-row">
        <code class="url-field">{{ snippets.connectorUrl }}</code>
        <Button
          size="small"
          severity="secondary"
          @click="copy('url', snippets.connectorUrl)"
        >
          <span class="material-icons" style="font-size: 16px">{{ copiedKey === 'url' ? 'check' : 'content_copy' }}</span>
          {{ copiedKey === 'url' ? $t('mcpConnect.copied') : $t('mcpConnect.copy') }}
        </Button>
      </div>

      <!-- Format -->
      <div class="section-label" style="margin-top: 24px">
        <span class="material-icons">tune</span>
        {{ $t('mcpConnect.formatLabel') }}
      </div>
      <SelectButton
        v-model="format"
        :options="formatOptions"
        option-label="label"
        option-value="key"
        :allow-empty="false"
        :aria-label="$t('mcpConnect.formatLabel')"
        class="format-select"
      />

      <div class="snippet-area">
        <div v-if="format === 'claudeAi'" class="snippet-instructions">
          {{ $t('mcpConnect.claudeAiSteps') }}
        </div>
        <div v-else class="snippet-block">
          <div class="snippet-toolbar">
            <Button
              text
              size="small"
              severity="secondary"
              @click="copy(format, currentSnippet)"
            >
              <span class="material-icons" style="font-size: 16px">{{ copiedKey === format ? 'check' : 'content_copy' }}</span>
              {{ copiedKey === format ? $t('mcpConnect.copied') : $t('mcpConnect.copy') }}
            </Button>
          </div>
          <pre class="snippet-code"><code>{{ currentSnippet }}</code></pre>
        </div>
      </div>

      <!-- Docs -->
      <div class="section-label" style="margin-top: 24px">
        <span class="material-icons">menu_book</span>
        {{ $t('mcpConnect.docsTitle') }}
      </div>
      <div class="docs-list">
        <p class="field-hint">• {{ $t('mcpConnect.docs.login') }}</p>
        <p class="field-hint">• {{ $t('mcpConnect.docs.scope') }}</p>
        <p class="field-hint">• {{ $t('mcpConnect.docs.rateLimit') }}</p>
        <p class="field-hint">• {{ $t('mcpConnect.docs.troubleshoot') }}</p>
      </div>

    </div>
  </Dialog>
</template>

<script setup>
/**
 * McpConnectDialog.vue
 *
 * Modal showing the MCP connector URL and per-client connection snippets
 * (Claude CLI/Desktop, GitHub Copilot, OpenCode, claude.ai) so a user can
 * hook up an external MCP client to the Crafter authoring server.
 *
 * @example
 * <McpConnectDialog :visible="showMcpDialog" @update:visible="showMcpDialog = $event" />
 */
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getMcpUrl, buildSnippets } from '../../utils/mcpConnect';
import { copyToClipboard } from '../../utils/clipboard';

defineProps({
  /** Controls Dialog visibility (parent-owned, non-mutating). */
  visible: { type: Boolean, default: false },
});
const emit = defineEmits(['update:visible']);

const { t } = useI18n();

const snippets = buildSnippets(getMcpUrl(import.meta.env.VITE_API_BASE_URL));

const format = ref('claudeCli');
const formatOptions = computed(() => [
  { key: 'claudeCli', label: t('mcpConnect.formats.claudeCli') },
  { key: 'claudeDesktop', label: t('mcpConnect.formats.claudeDesktop') },
  { key: 'copilot', label: t('mcpConnect.formats.copilot') },
  { key: 'opencode', label: t('mcpConnect.formats.opencode') },
  { key: 'claudeAi', label: t('mcpConnect.formats.claudeAi') },
]);

const currentSnippet = computed(() => snippets[format.value] || '');

const copiedKey = ref(null);
let copiedTimer = null;
async function copy(key, text) {
  await copyToClipboard(text);
  copiedKey.value = key;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => { copiedKey.value = null; }, 1500);
}

onUnmounted(() => clearTimeout(copiedTimer));
</script>

<style scoped>
/* ── Header ── */
.dialog-header-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.dialog-header-icon {
  font-size: 22px;
  color: #818cf8;
  margin-top: 2px;
  flex-shrink: 0;
}

.dialog-header-title h3 {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.dialog-header-title p {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
}

/* ── Body ── */
.dialog-body {
  padding: 4px 0 4px;
}

/* ── Section labels ── */
.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}

.section-label .material-icons {
  font-size: 14px;
}

/* ── URL row ── */
.url-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.url-field {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  background: #111827;
  border: 1px solid #4b5563;
  border-radius: 12px;
  font-family: monospace;
  font-size: 13px;
  color: #f3f4f6;
  white-space: nowrap;
  overflow-x: auto;
}

/* ── Format select ── */
.format-select {
  margin-bottom: 14px;
  display: flex;
  width: 100%;
}

/* 5 client options must fit the modal width without clipping: distribute the
   segments evenly and shrink padding/label so none overflow. */
.format-select :deep(.p-togglebutton) {
  flex: 1 1 0;
  min-width: 0;
  padding: 8px 6px;
  font-size: 12px;
  line-height: 1.2;
}

.format-select :deep(.p-togglebutton-label) {
  white-space: normal;
}

/* ── Snippet area ── */
.snippet-area {
  min-height: 230px;
}

.snippet-instructions {
  padding: 14px 16px;
  background: #1f2937;
  border: 1px solid #4b5563;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: #d1d5db;
}

.snippet-block {
  border: 1px solid #4b5563;
  border-radius: 12px;
  overflow: hidden;
}

.snippet-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 8px;
  background: #1f2937;
  border-bottom: 1px solid #4b5563;
}

.snippet-code {
  margin: 0;
  padding: 12px 16px;
  background: #111827;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #f3f4f6;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ── Docs ── */
.docs-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-hint {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}
</style>
